import { Box, useTheme } from "@mui/material";
import { useId, type ReactNode } from "react";
import loadingLogo from "../../media/logo/platformpuffin-loading.svg";
import { HangingProgressSign } from "./HangingProgressSign";

export interface LoadingProgressProps {
  value: number;
  message?: ReactNode;
  "aria-label"?: string;
}

export const LoadingProgress = ({ value, message, "aria-label": ariaLabel = "Progress" }: LoadingProgressProps) => {
  const theme = useTheme();
  const id = useId();
  const progress = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;

  return (
    <Box sx={{ position: "relative", width: 400, maxWidth: "100%", aspectRatio: message != null ? "1254 / 1430" : "1" }}>
      {message != null && <HangingProgressSign message={message} artworkId={id} />}
      <Box
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        sx={{ position: "absolute", top: 0, left: 0, width: "100%", aspectRatio: "1" }}
      >
        <Box component="img" src={loadingLogo} alt="" aria-hidden="true" sx={{ display: "block", width: "100%", filter: "grayscale(1)" }} />
        <Box
          component="img"
          src={loadingLogo}
          alt=""
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            clipPath: `inset(0 ${100 - progress}% 0 0)`,
            transition: "clip-path 1s ease-in-out",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
        <Box
          component="svg"
          viewBox="0 0 1254 1254"
          aria-hidden="true"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", transform: "translateX(20px)", overflow: "visible" }}
        >
          <defs>
            <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
              <stop stopColor={theme.palette.primary.main} />
              <stop offset="1" stopColor={theme.palette.secondary.main} />
            </linearGradient>
            <linearGradient id={`${id}-surface`} x1="0" y1="0" x2="0" y2="1">
              <stop stopColor={theme.palette.grey[800]} />
              <stop offset="1" stopColor={theme.palette.grey[900]} />
            </linearGradient>
            <filter id={`${id}-shadow`} x="-30%" y="-50%" width="160%" height="220%">
              <feDropShadow dx="0" dy="12" stdDeviation="14" floodOpacity="0.25" />
            </filter>
          </defs>
          <g transform="translate(25 -25) rotate(8 1035 450)" filter={`url(#${id}-shadow)`}>
            <rect x="880" y="375" width="310" height="150" rx="56" fill={`url(#${id}-surface)`} stroke={`url(#${id}-accent)`} strokeWidth="8" />
            <path d="M917 417l5 12 12 5-12 5-5 12-5-12-12-5 12-5z" fill={theme.palette.primary.light} />
            <text
              x="1045"
              y="477"
              textAnchor="middle"
              fill={theme.palette.common.white}
              fontSize="76"
              fontWeight="800"
              fontFamily={theme.typography.fontFamily}
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {Math.round(progress)}%
            </text>
          </g>
        </Box>
      </Box>
    </Box>
  );
};
